import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './Navigation.css';
import { 
    AccountCircle, 
    Notifications as NotificationsIcon,
    Delete as DeleteIcon 
} from '@mui/icons-material';
import {
    IconButton,
    Badge,
    Menu,
    MenuItem,
    ListItemText,
    ListItemIcon,
    Typography,
    Divider,
    Tooltip
} from '@mui/material';

function Navigation(props) {
    const { 
        isLoggedIn, 
        userRole, 
        userName, 
        openSurvey, 
        hasCompletedSurvey,
        spiIsVisible,      
        spiIsCompleted,
        prsIsVisible,
        charismaIsVisible,
        charismaIsCompleted,
        brandIsVisible, // NEW: Receive brandIsVisible prop
        isImpersonating,
        // NEW Props for notifications
        notifications,
        unreadCount,
        onMarkRead,
        onDeleteNotification
    } = props;
    
    const navigate = useNavigate();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [notificationAnchorEl, setNotificationAnchorEl] = useState(null); 

    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleLogout = () => {
        localStorage.clear(); 
        window.dispatchEvent(new Event('authChanged'));
        navigate('/home');
    };

    const toggleUserDropdown = (event) => {
        event.stopPropagation(); 
        setIsDropdownOpen(prev => !prev);
    };

    const handleNotificationMenuOpen = (event) => {
        setNotificationAnchorEl(event.currentTarget);
        if (unreadCount > 0) {
            onMarkRead(); 
        }
    };

    const handleNotificationMenuClose = () => {
        setNotificationAnchorEl(null);
    };

    const handleDeleteClick = (e, notificationId) => {
        e.stopPropagation(); 
        onDeleteNotification(notificationId);
    };

    const handleNotificationClick = (linkUrl) => {
        handleNotificationMenuClose();
        if (linkUrl) {
            navigate(linkUrl);
        }
    };


    return (
        <div>
            <div style={{ textAlign: 'center', padding: '10px' }}>
                <Link to={isLoggedIn ? "/dashboard" : "/home"} className="site-logo">
                    <img src="/radian.png" alt="Radian Logo" className="site-logo" />
                </Link>
            </div>
            <nav className="navigation-bar">
                {isLoggedIn ? (
                    <>
                        <div className="nav-group-left">
                            <Link to="/dashboard" className="nav-item">Dashboard</Link>
                            {spiIsVisible && (
                                spiIsCompleted ? (
                                    <Link to="/assessment/spi/results" className="nav-item">Identity</Link>
                                ) : (
                                    <Link to="/assessment/spi/survey" className="nav-item">Identity</Link>
                                )
                            )}

                            {prsIsVisible && (
                                <Link to="/assessment/prs/config" className="nav-item">Reputation</Link>
                            )}
                             {/* NEW: Conditionally render Brand link */}
                            {brandIsVisible && (
                                <Link to="/brand-clarity" className="nav-item">Brand</Link>
                            )}

                            {charismaIsVisible && (
                                charismaIsCompleted ? (
                                    <Link to="/assessment/charisma/results" className="nav-item">Charisma</Link>
                                ) : (
                                    <Link to="/assessment/charisma/survey" className="nav-item">Charisma</Link>
                                )
                            )}
                            
                            {openSurvey && openSurvey.id && !hasCompletedSurvey && (
                                <Link to={`/survey-rounds/${openSurvey.id}/questions`} className="nav-item">
                                    360° Round Open
                                </Link>
                            )}

                            {userRole === '1' && (
                                <>
                                    <Link to="/reports-landing" className="nav-item">360° Reports</Link>
                                    <Link to="/survey-management" className="nav-item">360° Management</Link>
                                    <Link to="/cohort-management" className="nav-item">Cohort Management</Link>
                                    <Link to="/team-management" className="nav-item">Team Management</Link>
                                    <Link to="/user-management" className="nav-item">User Management</Link>
                                </>
                            )}
                            {userRole === '2' && (
                                <>
                                    <Link to="/reports-landing" className="nav-item">360° Reports</Link>
                                    <Link to="/survey-management" className="nav-item">360° Management</Link>
                                    <Link to="/team-management" className="nav-item">Team Management</Link>
                                </>
                            )}
                            {userRole === '3' && (
                                <Link to="/reports-landing" className="nav-item">360° Reports</Link>
                            )}

                        </div>
                        
                        <div className="nav-group-right">
                            <div className="nav-item user-dropdown-container" ref={dropdownRef}>
                                <button className="user-dropdown-button" onClick={toggleUserDropdown} aria-haspopup="true" aria-expanded={isDropdownOpen}>
                                    <IconButton color="inherit" aria-label="menu" sx={{ p: '4px' }}>
                                        <AccountCircle />
                                    </IconButton>
                                    {userName || 'User'}
                                </button>
                                {isDropdownOpen && (
                                    <div className="user-dropdown open">
                                        <Link to="/profile" onClick={() => setIsDropdownOpen(false)}>View/Edit Profile</Link>
                                        <Link to="/about" onClick={() => setIsDropdownOpen(false)}>About Radian</Link>
                                        <button onClick={handleLogout}>Logout</button>
                                    </div>
                                )}
                            </div>
                            <Tooltip title="Notifications">
                                <IconButton
                                    color="inherit"
                                    aria-label="show new notifications"
                                    onClick={handleNotificationMenuOpen}
                                    onMouseOver={handleNotificationMenuOpen}
                                >
                                    <Badge badgeContent={unreadCount} color="error">
                                        <NotificationsIcon />
                                    </Badge>
                                </IconButton>
                            </Tooltip>
                            <Menu
                                anchorEl={notificationAnchorEl}
                                open={Boolean(notificationAnchorEl)}
                                onClose={handleNotificationMenuClose}
                                MenuListProps={{ 
                                    onMouseLeave: handleNotificationMenuClose,
                                    'aria-labelledby': 'notifications-button',
                                }}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                sx={{'.MuiPaper-root': {maxHeight: 400, width: 360, mt: 1}}}
                            >
                                <Typography variant="h6" sx={{px: 2, pt:1}}>Notifications</Typography>
                                <Divider sx={{mb:1}}/>
                                {notifications && notifications.length > 0 ? (
                                    notifications.map((notification) => (
                                        <MenuItem 
                                            key={notification.notification_id} 
                                            onClick={() => handleNotificationClick(notification.link_url)} 
                                            sx={{whiteSpace: 'normal', opacity: notification.is_read ? 0.6 : 1, alignItems: 'flex-start'}}
                                        >
                                            <ListItemText 
                                                primaryTypographyProps={{sx: {fontWeight: notification.is_read ? 'normal' : 'bold'}}}
                                                primary={notification.message}
                                                secondary={new Date(notification.created_at).toLocaleString()}
                                            />
                                            <ListItemIcon sx={{minWidth: 'auto', pl: 1, mt: 0.5}}>
                                                <Tooltip title="Delete Notification">
                                                    <IconButton
                                                        edge="end"
                                                        aria-label="delete"
                                                        onClick={(e) => handleDeleteClick(e, notification.notification_id)}
                                                        size="small"
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </ListItemIcon>
                                        </MenuItem>
                                    ))
                                ) : (
                                    <MenuItem disabled>
                                        <ListItemText primary="No new notifications." />
                                    </MenuItem>
                                )}
                            </Menu>

                        </div>

                    </>
                ) : (
                    <div className="nav-group-right">
                        <Link to="/about" className="nav-item logout">About Radian</Link>
                        <Link to="/login" className="nav-item logout">Login</Link>
                    </div>
                )}
                <div className="responsive-menu-button" onClick={() => {
                    const nav = document.querySelector('.navigation-bar');
                    if (nav) nav.classList.toggle('responsive');
                }}>
                    &#9776;
                </div>
            </nav>
        </div>
    );
}

export default Navigation;
